import React, { useEffect, useRef } from 'react';
import './Branding.css';

const Branding = () => {
  const cardsRef = useRef([]);
  const hamburgerRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileCloseRef = useRef(null);
  const overlayRef = useRef(null);
  const togglesRef = useRef([]);

  

  useEffect(() => {
    // Animate cards on scroll
    const cards = cardsRef.current;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add("show");
        }
      });
    }, { threshold: 0.2 });

    cards.forEach(card => {
      if (card) observer.observe(card);
    });

    // Mobile menu functionality
    const hamburger = hamburgerRef.current;
    const mobileMenu = mobileMenuRef.current;
    const mobileClose = mobileCloseRef.current;
    const overlay = overlayRef.current;
    const toggles = togglesRef.current;

    const openMenu = () => {
      mobileMenu.classList.add("open");
      overlay.classList.add("open");
    };

    const closeMenu = () => {
      mobileMenu.classList.remove("open");
      overlay.classList.remove("open");
    };

    const toggleDropdown = (toggle) => {
      toggle.nextElementSibling.classList.toggle("open");
    };

    if (hamburger) hamburger.addEventListener("click", openMenu);
    if (mobileClose) mobileClose.addEventListener("click", closeMenu);
    if (overlay) overlay.addEventListener("click", closeMenu);

    toggles.forEach(toggle => {
      if (toggle) {
        toggle.addEventListener("click", () => toggleDropdown(toggle));
      }
    });

    // Cleanup
    return () => {
      cards.forEach(card => {
        if (card) observer.unobserve(card);
      });
      
      if (hamburger) hamburger.removeEventListener("click", openMenu);
      if (mobileClose) mobileClose.removeEventListener("click", closeMenu);
      if (overlay) overlay.removeEventListener("click", closeMenu);
      
      toggles.forEach(toggle => {
        if (toggle) {
          toggle.removeEventListener("click", () => toggleDropdown(toggle));
        }
      });
    };
  }, []);

  const addToCardsRef = (el) => {
    if (el && !cardsRef.current.includes(el)) {
      cardsRef.current.push(el);
    }
  };

  const addToTogglesRef = (el) => {
    if (el && !togglesRef.current.includes(el)) {
      togglesRef.current.push(el);
    }
  };

  return (
    <div className="branding-page">
      

      {/* Hero */}
      <section className="br-hero">
        <h1>Branding</h1>
        <p>Crafting powerful identities that inspire trust, loyalty, and growth.</p>
      </section>

      {/* Mission */}
      <section className="br-mission" id="mission">
        <div className="br-mission-content">
          <h2>Our Mission</h2>
          <p>
            At Oakya IT Services, we go beyond visuals — we build complete brand experiences. 
            From research and strategy to launch and evolution, our mission is to empower your business 
            with a brand identity that resonates deeply and stands the test of time.
          </p>
        </div>
      </section>

      {/* Importance */}
      <section className="br-importance" id="importance">
        <div className="br-importance-content">
          <h2>The Importance of Branding</h2>
          <div className="br-importance-grid">
            <div className="br-importance-card" ref={addToCardsRef}>
              <img src="/images/id.webp" alt="Identity" />
              <h3>Identity & Recognition</h3>
              <p>Establishes a memorable presence across all touchpoints.</p>
            </div>
            <div className="br-importance-card" ref={addToCardsRef}>
              <img src="/images/trust.webp" alt="Trust" />
              <h3>Trust & Loyalty</h3>
              <p>Builds long-term relationships with your audience.</p>
            </div>
            <div className="br-importance-card" ref={addToCardsRef}>
              <img src="/images/connection.webp" alt="Connection" />
              <h3>Emotional Connection</h3>
              <p>Creates resonance that turns customers into advocates.</p>
            </div>
            <div className="br-importance-card" ref={addToCardsRef}>
              <img src="/images/market-analysis.webp" alt="Differentiation" />
              <h3>Market Differentiation</h3>
              <p>Sets you apart from competitors in a crowded marketplace.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Branding Steps */}
      <section className="br-steps" id="steps">
        <h2>Our Branding Process</h2>
        <div className="br-steps-container">
          <div className="br-step-card" ref={addToCardsRef}>
            <img src="/images/understanding.webp" alt="Discovery" />
            <h3>1. Understanding & Discovery</h3>
            <p>Map your identity, goals, audience, and market insights.</p>
          </div>
          <div className="br-step-card" ref={addToCardsRef}>
            <img src="/images/strategy.webp" alt="Strategy" />
            <h3>2. Strategic Brand Blueprint</h3>
            <p>Define voice, positioning, and messaging architecture.</p>
          </div>
          <div className="br-step-card" ref={addToCardsRef}>
            <img src="/images/visual-identities.webp" alt="Visual Identity" />
            <h3>3. Visual Identity & Expression</h3>
            <p>Create visual assets and guidelines for consistency.</p>
          </div>
          <div className="br-step-card" ref={addToCardsRef}>
            <img src="/images/messages.webp" alt="Messaging" />
            <h3>4. Messaging Development</h3>
            <p>Craft unified, emotive language across all touchpoints.</p>
          </div>
          <div className="br-step-card" ref={addToCardsRef}>
            <img src="/images/review.webp" alt="Assets" />
            <h3>5. Asset Creation & Application</h3>
            <p>Produce branded collateral and ready-to-use templates.</p>
          </div>
          <div className="br-step-card" ref={addToCardsRef}>
            <img src="/images/product-launch.webp" alt="Launch" />
            <h3>6. Launch, Rollout & Experience</h3>
            <p>Guide adoption and orchestrate your external reveal.</p>
          </div>
          <div className="br-step-card" ref={addToCardsRef}>
            <img src="/images/monitoring.webp" alt="Evolution" />
            <h3>7. Monitoring & Evolution</h3>
            <p>Measure impact and iterate for long-term relevance.</p>
          </div>
        </div>
      </section>

      {/* Why Partner With Us */}
      <section className="br-why-us">
        <h2>Why Partner With Oakya?</h2>
        <div className="br-why-grid">
          <div className="br-why-card" ref={addToCardsRef}>
            <h3>Trusted Expertise</h3>
            <p>Proven track record in crafting impactful brand identities.</p>
          </div>
          <div className="br-why-card" ref={addToCardsRef}>
            <h3>Creative Edge</h3>
            <p>We blend creativity with strategy for powerful storytelling.</p>
          </div>
          <div className="br-why-card" ref={addToCardsRef}>
            <h3>Growth Driven</h3>
            <p>Your brand isn't static — we evolve it for sustainable growth.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="br-cta">
        <h2>Ready to Transform Your Brand?</h2>
        <a className="br-cta-btn" href="mailto:info@oakyaitservices.com">Book Your Free Consultation</a>
                 

      </section>
    </div>
  );
};

export default Branding;